<template>
  <div class="customize-assessment-container">
    <!-- Button to open the modal -->
    <b-button
      variant="primary"
      size="lg"
      @click="openCustomizeModal"
      class="btn_lg customize-btn"
      :aria-label="buttonLabel"
    >
      {{ buttonText }}
    </b-button>

    <!-- Modal for customization -->
    <b-modal
      ref="customizeModal"
      id="modal-scrollable"
      class="modal__v22_2"
      :title="modalTitle"
      size="xl"
      :hide-header="false"
      :close-on-backdrop="false"
      :close-on-esc="false"
      centered
    >
      <div slot="modal-header" class="w-100">
        <div class="row">
          <div class="col-2 hd">
            <div class="ic_container"></div>
          </div>
          <div class="col-8 hd">
            <div class="hd_txt text-center w-100" style="margin-left: 0px">
              <span>{{ modalTitle }}</span>
            </div>
          </div>
          <div class="col-2 btn_container">
            <button
              class="btn_close"
              aria-label="close"
              title="close the popup"
              @click="closeCustomizeModal"
            >
              <span class="ic-close"></span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="loading" class="text-center p-3">
        <i class="fa fa-spinner fa-pulse fa-2x"></i>
        <p>{{ loadingMessage }}</p>
      </div>
      <div
        v-else-if="!loading && activityId && externalToken"
        class="iframe-container"
      >
        <iframe
          ref="customizeIframe"
          :src="iframeUrl"
          width="100%"
          height="100%"
          frameborder="0"
          allowfullscreen
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
        />
      </div>
      <div slot="modal-footer" class="w-100">
        <b-button
          @click="closeCustomizeModal"
          class="btn button-primary float-right"
          >Close</b-button
        >
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import { mapActions } from "vuex";

export default {
  name: "CustomizeAssessment",
  props: {
    buttonText: {
      type: String,
      default: "Customize Knowledge Check",
    },
    buttonLabel: {
      type: String,
      default: "Customize Knowledge Check",
    },
    modalTitle: {
      type: String,
      default: "Customize Knowledge Check",
    },
    courseId: {
      type: String,
      required: true,
    },
    mcId: {
      type: String,
      required: true,
    },
    entityType: {
      type: String,
      required: true,
    },
    entityUuid: {
      type: String,
      required: true,
    },
    sessionId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      activityId: null,
      loading: false,
      loadingMessage: "",
      error: "",
      externalToken: null,
      localAiUrl: "https://localhost:8081/",
      stagingAiUrl: "https://aiqa.pragyasystems.com/",
      prodAiUrl: "https://ai.pragyasystems.com/",
      tokenExpirationTimer: null,
      tokenExpirationTime: 0,
      lastNotifications: {}, // Track last notification times by type
      debounceTime: 1000, // Minimum time between similar notifications (1 second)
      modalClosed: false, // Track if modal is closed to prevent duplicate notifications
      notificationInProgress: false, // Flag to prevent concurrent notifications
    };
  },
  computed: {
    isProd() {
      const hostname = window.location.hostname
        .toLowerCase()
        .replace(/\.$/, ""); // normalize

      // only matches exact domain or www
      return /^(www\.)?pragyasystems\.com$/.test(hostname);
    },
    aiUrlToUse() {
      if (this.isProd) {
        return this.prodAiUrl;
      } else {
        return this.stagingAiUrl;
      }
    },
    iframeUrl() {
      return `${this.aiUrlToUse}/#/iframe/itemcustomization/?activityId=${
        this.activityId
      }&assessmentId=${this.entityUuid}&courseId=${this.courseId}&token=${
        this.externalToken
      }&heading=${encodeURIComponent(this.modalTitle)}`;
    },
    authenticateApiUrl() {
      return `${this.aiUrlToUse}/api/login/external`;
    },
  },
  created() {
    // Create a bound version of the method to ensure the same reference is used for adding and removing
    this.boundIframeMessageHandler = this.handleIframeMessage.bind(this);
  },
  methods: {
    ...mapActions(["loadCourseDetail", "loadEntityDetail", "launchAssessment"]),

    async loadCourse() {
      try {
        this.loadingMessage = "Fetching item data...";
        if(this.courseId) {
          await this.loadCourseDetail(this.courseId);
        }

        const entityDetailInfo = {
          entityType: this.entityType,
          entityUuid: this.entityUuid,
        };

        const res = await this.loadEntityDetail(entityDetailInfo);
        if (res && res.type === "ASSESSMENT") {
          this.loadLaunchAssessmentWidget = true;
          await this.initLaunchAssessment();
        }
      } catch (error) {
        console.error("Error loading course:", error);
        // Display a user-friendly error message
        this.showNotification({
          type: "error",
          title: "Error Loading Course",
          text: "Failed to load course data. Please try again.",
        });
        // Close modal on critical errors
        this.closeCustomizeModal();
        throw error;
      }
    },

    async initLaunchAssessment() {
      try {
        const res = await this.launchAssessment({
          uuid: this.entityUuid,
          courseId: this.courseId?this.courseId:this.mcId,
          sessionId: this.sessionId,
        });
        if (res) {
          const signedRequest = JSON.parse(res.signedRequest);
          this.activityId = signedRequest.request.activity_template_id;
        }
      } catch (error) {
        console.error("Error launching assessment:", error);

        // Check if it's an auth error
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 403)
        ) {
          this.showNotification({
            type: "error",
            title: "Authentication Error",
            text: "Your session has expired. Please log in again.",
          });
          this.closeCustomizeModal();
        } else {
          this.showNotification({
            type: "error",
            title: "Launch Error",
            text: "Failed to initialize assessment. Please try again.",
          });
          this.closeCustomizeModal();
        }
        throw error;
      }
    },

    async authenticateUser() {
      try {
        const userEmail = this.$store.state.user.userObj.eMail;
        const res = await axios.post(this.authenticateApiUrl, {
          eMail: userEmail,
        });
        this.externalToken = res.data.token;

        // Parse token to get expiration time
        if (this.externalToken) {
          this.setupTokenExpiration(this.externalToken);
        }
      } catch (error) {
        console.error("Authentication error:", error);
        // Handle authentication failures
        this.showNotification({
          type: "error",
          title: "Authentication Failed",
          text: "Unable to authenticate. Please try again later.",
        });
        this.closeCustomizeModal();
        throw error;
      }
    },

    setupTokenExpiration(token) {
      // Clear any existing timer
      if (this.tokenExpirationTimer) {
        clearInterval(this.tokenExpirationTimer);
      }

      try {
        // Parse JWT token to get expiration time
        const payload = JSON.parse(atob(token.split(".")[1]));
        this.tokenExpirationTime = payload.exp;

        // Set up timer to check token expiration
        this.tokenExpirationTimer = setInterval(() => {
          this.checkTokenExpiration();
        }, 30000); // Check every 30 seconds
      } catch (error) {
        console.error("Error parsing token:", error);
      }
    },

    checkTokenExpiration() {
      if (!this.tokenExpirationTime || this.modalClosed) return;

      const now = Math.floor(Date.now() / 1000);
      const timeRemaining = this.tokenExpirationTime - now;

      // If token is expired or about to expire very soon (30 seconds)
      if (timeRemaining <= 30) {
        this.handleTokenExpired();
      }
    },

    handleTokenExpired() {
      if (this.modalClosed) return;

      this.closeCustomizeModal();
      this.showNotification({
        type: "error",
        title: "Session Expired",
        text: "Your session has expired. Please try again.",
      });
    },

    showNotification(notification) {
      // Check for notification in progress to prevent multiple notifications at once
      if (this.notificationInProgress) return;
      this.notificationInProgress = true;

      // Implement debouncing to prevent duplicate notifications
      const messageType = notification.title;
      const currentTime = Date.now();

      // Check if we've shown this notification type recently
      if (
        this.lastNotifications[messageType] &&
        currentTime - this.lastNotifications[messageType] < this.debounceTime
      ) {
        this.notificationInProgress = false;
        return;
      }

      // Update the last notification time for this type
      this.lastNotifications[messageType] = currentTime;

      // Create a unique ID for this notification
      const notificationId = `${messageType.replace(
        /\s+/g,
        "-"
      )}-${currentTime}`;

      // Show notification with unique ID
      this.$notify({
        group: "notify",
        type: notification.type,
        title: notification.title,
        text: notification.text,
        duration: 5000,
        id: notificationId,
      });

      // Reset flag after a short delay
      setTimeout(() => {
        this.notificationInProgress = false;
      }, 100);
    },

    openCustomizeModal() {
      this.modalClosed = false;

      if (this.$refs.customizeModal) {
        this.$refs.customizeModal.show();
      }
      this.$emit("modal-opened", this.courseId);

      // Set loading before both start
      this.loading = true;
      this.loadingMessage = "Initializing...";

      // Run both async functions in parallel
      Promise.all([this.authenticateUser(), this.loadCourse()])
        .catch((error) => {
          console.error("Error in parallel execution:", error);
          // Don't close modal here - individual error handlers will do that if needed
        })
        .finally(() => {
          this.loading = false;
        });
    },

    closeCustomizeModal() {
      // Set flag to prevent further notifications
      this.modalClosed = true;

      // Clear token expiration timer
      if (this.tokenExpirationTimer) {
        clearInterval(this.tokenExpirationTimer);
        this.tokenExpirationTimer = null;
      }

      if (this.$refs.customizeModal) {
        this.$refs.customizeModal.hide();
      }
      this.$emit("modal-closed");

      // Reset state when modal is closed
      this.selectedCareer = "";
      this.customizedItems = [];
      this.activityId = null;
      this.externalToken = null;
    },

    handleIframeMessage(event) {
      // Skip processing if modal is already closed
      if (this.modalClosed) return;

      // In production, validate event.origin
      if (this.isProd && event.origin !== this.aiUrlToUse) return;

      if (!event.data || !event.data.type) return;

      const messageType = event.data.type;

      if (messageType === "TOKEN_EXPIRED" || messageType === "UNAUTHORIZED") {
        this.closeCustomizeModal();
        this.showNotification({
          type: "error",
          title: "Session Expired",
          text:
            event.data.message || "Your session has expired. Please try again.",
        });
      } else if (messageType === "URL_CHANGED") {
        if (event.data.url && event.data.url.includes("/login")) {
          this.closeCustomizeModal();
          this.showNotification({
            type: "error",
            title: "Session Expired",
            text: "Your session has expired. Please try again.",
          });
        }
      } else if (messageType === "ERROR") {
        this.showNotification({
          type: "error",
          title: "Error",
          text: event.data.message || "An error occurred",
        });
      } else if (messageType === "TOKEN_REFRESH_REQUIRED") {
        // Instead of refreshing, just handle as expired
        this.handleTokenExpired();
      } else if (messageType === "ITEM_CUSTOMIZATION_SAVED") {
        // Show success notification
        this.showNotification({
          type: "success",
          title: "Success",
          text: "Customized KC saved",
        });
        // Close the modal
        this.closeCustomizeModal();
      }
    },
  },
  mounted() {
    // Listen for messages from the iframe using the bound handler
    window.addEventListener("message", this.boundIframeMessageHandler);
  },

  beforeUnmount() {
    // Clean up the event listener and timer
    window.removeEventListener("message", this.boundIframeMessageHandler);
    if (this.tokenExpirationTimer) {
      clearInterval(this.tokenExpirationTimer);
    }
  },
};
</script>

<style scoped>
.customize-assessment-container {
  display: inline-block;
}

.customize-btn {
  margin-left: 8px;
}

.iframe-container {
  width: 100%;
  height: 600px;
  overflow-y: auto;
}

.btn_close {
  background: transparent;
  border: none;
  float: right;
  cursor: pointer;
  padding: 5px;
}

.ic-close:before {
  content: "×";
  font-size: 24px;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/app/course/components/customize-assessment/CustomizeAssessment.vue